import { MathManager } from '@payments-checkout/shared/helpers/index';
import { DigitalWalletsTypeAnaliticaEnum } from '@payments-checkout/shared/plugins/analitica/analitica.enum';
import {
  StatusPagePaymentMethod,
  StatusPageStatus,
  analiticaStatusPageEvents,
} from '@payments-checkout/shared/plugins/analitica/statusPage.events';
import type { IntlShape } from 'react-intl';
import type { SessionStatusResponse } from '../../../types/sessionStatus.types';
import {
  resolveCreditCardState,
  type CreditCardFeedback,
  type CreditCardState,
} from '../../creditCardStatus';
import {
  isDigitalWalletPayment,
  normalizeWalletType,
  resolveDigitalWalletState,
  resolveTransactionType,
  type DigitalWalletState,
} from '../../digitalWalletStatus';
import type { BoletoPaymentInfo, PixPaymentInfo } from '../../paymentInfoDisplay/types';
import { isExpired } from '../../trackingStatus/expirationHelper';
import { formatPaymentDate } from '../../trackingStatus/statusDate';

const DIGITAL_WALLET_LABEL_BY_TYPE = {
  apple_pay: DigitalWalletsTypeAnaliticaEnum.APPLE_PAY,
  google_pay: DigitalWalletsTypeAnaliticaEnum.GOOGLE_PAY,
  click_to_pay: DigitalWalletsTypeAnaliticaEnum.CLICK_TO_PAY,
} as const;

const TRANSACTION_TYPE_TO_PAYMENT_METHOD: Record<'credit' | 'debit', StatusPagePaymentMethod> = {
  credit: 'credito',
  debit: 'debito',
};

const CARD_STATE_TO_STATUS: Record<CreditCardState | DigitalWalletState, StatusPageStatus> = {
  processing: 'em_analise',
  approved: 'pago',
  refused: 'recusado',
};

interface TrackStatusPageViewedParams {
  sessionData?: SessionStatusResponse | null;
  isWallet: boolean;
  isCard: boolean;
  orderNotPaid: boolean;
  paymentMethod: string;
}

function trackDigitalWalletViewed(
  sessionData: SessionStatusResponse,
  paymentMethod: string,
  state: DigitalWalletState
) {
  const walletType = normalizeWalletType(
    sessionData.payments?.[0]?.card?.tokenization_method
  );
  const digitalWallet = walletType
    ? DIGITAL_WALLET_LABEL_BY_TYPE[walletType]
    : undefined;

  if (!digitalWallet) return;

  const transactionType = resolveTransactionType(paymentMethod);

  analiticaStatusPageEvents.events.statusPage.pageViewed({
    paymentMethod: TRANSACTION_TYPE_TO_PAYMENT_METHOD[transactionType],
    status: CARD_STATE_TO_STATUS[state],
    digitalWallet,
  });
}

function trackCreditCardViewed(state: CreditCardState) {
  analiticaStatusPageEvents.events.statusPage.pageViewed({
    paymentMethod: 'credito',
    status: CARD_STATE_TO_STATUS[state],
  });
}

function trackPixViewed(sessionData: SessionStatusResponse, orderNotPaid: boolean) {
  if (!orderNotPaid) {
    analiticaStatusPageEvents.events.statusPage.pageViewed({ paymentMethod: 'pix', status: 'pago' });
    return;
  }

  const expired = isExpired(sessionData.payments?.[0]?.pix?.expires_at);
  analiticaStatusPageEvents.events.statusPage.pageViewed({
    paymentMethod: 'pix',
    status: expired ? 'expirado' : 'aguardando_pagamento',
  });
}

function trackBoletoViewed(sessionData: SessionStatusResponse, orderNotPaid: boolean) {
  if (!orderNotPaid) {
    analiticaStatusPageEvents.events.statusPage.pageViewed({ paymentMethod: 'boleto', status: 'pago' });
    return;
  }

  const expired = isExpired(sessionData.payments?.[0]?.boleto?.due_at);
  analiticaStatusPageEvents.events.statusPage.pageViewed({
    paymentMethod: 'boleto',
    status: expired ? 'expirado' : 'aguardando_pagamento',
  });
}

export interface StatusPageDisplay {
  paymentMethod: string;
  orderNotPaid: boolean;
  isWallet: boolean;
  isCard: boolean;
  hasDelivery: boolean;
  statusLabel: string;
  statusIcon: 'circle-money-in' | 'check';
  statusIconColor: 'neutral' | 'brand';
  amount: string;
  subtitle?: string;
}

function addSpaceAfterCurrencySymbol(formattedAmount: string): string {
  return formattedAmount.replace(/^(-?)(\D+)/, '$1$2 ');
}

export function resolveStatusPageDisplay(
  sessionData: SessionStatusResponse | null | undefined,
  formatMessage: IntlShape['formatMessage'],
  formatDate: IntlShape['formatDate']
): StatusPageDisplay {
  const paymentMethod = sessionData?.payments?.[0]?.payment_method ?? 'boleto';
  const orderNotPaid = sessionData?.order_status !== 'paid';
  const isWallet = isDigitalWalletPayment(sessionData?.payments?.[0]);
  const isCard = paymentMethod === 'credit_card';
  const hasDelivery = !!(
    sessionData?.order_status === 'paid' && sessionData?.delivery?.tracking_code
  );

  const amount = addSpaceAfterCurrencySymbol(
    MathManager.formatCurrency({
      value: (sessionData?.summary?.total ?? 0) / 100,
    })
  );
  const subtitle = formatPaymentDate(sessionData?.paid_at, formatDate, formatMessage);

  return {
    paymentMethod,
    orderNotPaid,
    isWallet,
    isCard,
    hasDelivery,
    statusLabel: formatMessage({
      id: orderNotPaid ? 'statusPage.waitingPayment' : 'statusPage.purchaseApproved',
    }),
    statusIcon: orderNotPaid ? 'circle-money-in' : 'check',
    statusIconColor: orderNotPaid ? 'neutral' : 'brand',
    amount,
    subtitle,
  };
}

export function buildPixOrBoletoPaymentInfo(
  sessionData: SessionStatusResponse,
  paymentMethod: string,
  isOrderPaid: boolean
): PixPaymentInfo | BoletoPaymentInfo | undefined {
  const isPix = paymentMethod === 'pix';
  const isBoleto = paymentMethod === 'boleto';

  if (!isOrderPaid || (!isPix && !isBoleto)) return undefined;

  return {
    paymentMethod: isPix ? 'pix' : 'boleto',
    customerName: sessionData?.customer?.name || '',
    customerDocument: sessionData?.customer?.document || '',
  };
}

interface PixOrBoletoFeedbackParams
  extends Pick<
    StatusPageDisplay,
    'statusLabel' | 'statusIcon' | 'statusIconColor' | 'amount' | 'subtitle'
  > {
  sessionData: SessionStatusResponse;
  orderNotPaid: boolean;
  formatMessage: IntlShape['formatMessage'];
  onPrintReceipt: () => void;
}

export function buildPixOrBoletoFeedback({
  sessionData,
  orderNotPaid,
  statusLabel,
  statusIcon,
  statusIconColor,
  amount,
  subtitle,
  formatMessage,
  onPrintReceipt,
}: PixOrBoletoFeedbackParams): CreditCardFeedback {
  const orderId = `${formatMessage({ id: 'statusPage.orderId' })} ${sessionData.order_id.toUpperCase()}`;
  const offeredBy = `${formatMessage({ id: 'statusPage.creditCard.soldBy' })} ${sessionData?.merchant?.name}`;

  const baseFeedback: CreditCardFeedback = {
    title: statusLabel,
    amount,
    subtitle,
    orderId,
    icon: statusIcon,
    variant: statusIconColor,
    offeredBy,
  };

  if (orderNotPaid) {
    return baseFeedback;
  }

  return {
    ...baseFeedback,
    actionLabel: formatMessage({ id: 'statusPage.creditCard.downloadReceipt' }),
    actionDisabled: false,
    onAction: onPrintReceipt,
  };
}

export function trackStatusPageViewed({
  sessionData,
  isWallet,
  isCard,
  orderNotPaid,
  paymentMethod,
}: TrackStatusPageViewedParams) {
  if (!sessionData) return;

  if (isWallet) {
    return trackDigitalWalletViewed(
      sessionData,
      paymentMethod,
      resolveDigitalWalletState(sessionData.order_status)
    );
  }

  if (isCard) {
    return trackCreditCardViewed(resolveCreditCardState(sessionData.order_status));
  }

  if (paymentMethod === 'pix') return trackPixViewed(sessionData, orderNotPaid);
  if (paymentMethod === 'boleto') return trackBoletoViewed(sessionData, orderNotPaid);
}
